
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, FolderOpen, Wrench, Package, Users, Truck, Building2,
  UserSquare2, PanelTop, CalendarClock, PanelLeft, Crown, Settings as SettingsIcon,
  Store as StoreIcon, Wand2,
} from 'lucide-react';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent, SidebarGroupLabel,
  SidebarHeader, SidebarMenu, SidebarMenuButton, SidebarMenuItem, SidebarMenuSub,
  SidebarMenuSubButton, SidebarMenuSubItem,
} from '@/components/ui/sidebar';
import { useCurrentOrganization } from '@/contexts/OrganizationContext';
import { useHasFeature } from '@/hooks/useOrgPlan';
import { useSidebarCounts } from '@/hooks/useSidebarCounts';
import { useIsAppAdmin } from '@/hooks/useIsAppAdmin';
import { Logo } from '@/components/brand/Logo';

/**
 * Sidebar a 3 cluster semantici:
 *  - LAVORO: attività quotidiana (Dashboard, Progetti, Clienti, Agenda)
 *  - CONFIGURATORE: lavoro sulle stratigrafie e sul catalogo (Parete, Controsoffitto, Materiali, Fornitori)
 *  - AMMINISTRAZIONE: setup org, impostazioni tecniche, team, billing
 *
 * "Importa listino" e' raggiungibile dalla pagina Materiali e dall'eventuale
 * Setup Checklist in Impostazioni; e' un'azione di setup non quotidiana e quindi
 * NON occupa una voce top-level del sidebar.
 *
 * UserMenu (Profilo + Impostazioni + Esci) e' in `Header.tsx` (UserMenu.tsx).
 */
const AppSidebar = () => {
  const location = useLocation();
  const { currentRole } = useCurrentOrganization();
  const canManageTeam = currentRole === 'owner' || currentRole === 'admin';
  const isOwner = currentRole === 'owner';
  // Membri visibili (lista in read-only) anche per SUP/TEC/VIE — utile per coordinarsi
  // col team. Le azioni (invita/modifica/rimuovi) sono gated dentro Members.tsx.
  // Sotto piani Solo/Studio la voce mostra upsell se il ruolo è owner/admin, altrimenti
  // resta una lista di soli colleghi.
  const canSeeMembers = !!currentRole;
  // Feature-gate: pagina Membri disponibile solo su piano Team (vedi PRICING.md).
  const hasMembersFeature = useHasFeature('members');
  const counts = useSidebarCounts();
  const { isAdmin: isAppAdmin } = useIsAppAdmin();

  type NavItem = {
    name: string;
    href: string;
    icon: typeof LayoutDashboard;
    badge?: number;
    badgeTitle?: string;
  };

  // ============================================================================
  // Cluster LAVORO — voci ad alto traffico quotidiano
  // ============================================================================
  const navWork: NavItem[] = [{
    name: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard,
    badge: counts.openEstimates > 0 ? counts.openEstimates : undefined,
    badgeTitle: counts.openEstimates > 0 ? `${counts.openEstimates} preventivi aperti` : undefined,
  }, {
    name: 'Progetti',
    href: '/projects',
    icon: FolderOpen,
    badge: counts.activeProjects > 0 ? counts.activeProjects : undefined,
    badgeTitle: counts.activeProjects > 0 ? `${counts.activeProjects} progetti attivi` : undefined,
  }, {
    name: 'Clienti',
    href: '/customers',
    icon: UserSquare2,
    badge: counts.customers > 0 ? counts.customers : undefined,
    badgeTitle: counts.customers > 0 ? `${counts.customers} clienti in anagrafica` : undefined,
  }, {
    name: 'Agenda',
    href: '/agenda',
    icon: CalendarClock,
    badge: counts.pendingFollowUps > 0 ? counts.pendingFollowUps : undefined,
    badgeTitle: counts.pendingFollowUps > 0 ? `${counts.pendingFollowUps} attività da fare` : undefined,
  }];

  // ============================================================================
  // Cluster CONFIGURATORE — catalogo + builder
  // (Parete e Controsoffitto vivono sotto "Configura ▾", poi Materiali e Fornitori)
  // ============================================================================
  const configureActive = location.pathname === '/configurator' || location.pathname === '/ceilings';

  const navCatalog: NavItem[] = [{
    name: 'Materiali',
    href: '/materials',
    icon: Package,
  }, ...(canManageTeam ? [{
    name: 'Fornitori',
    href: '/settings/suppliers',
    icon: Truck,
  }] : [])];

  // ============================================================================
  // Cluster AMMINISTRAZIONE — setup raro/periodico
  // ============================================================================
  const navAdmin: NavItem[] = [
    ...(canManageTeam ? [{
      name: 'Organizzazione',
      href: '/settings/organization',
      icon: Building2,
    }] : []),
    // Impostazioni: voce mancante storicamente nel sidebar, ora aggiunta come
    // hub per le configurazioni tecniche (sfridi, finiture Q1-Q4, viti, manodopera).
    {
      name: 'Impostazioni',
      href: '/settings',
      icon: SettingsIcon,
    },
    // Membri: voce visibile a TUTTI i ruoli (owner/admin/supervisor/technician/viewer).
    // Owner/admin vedono CTA gestione team; ruoli non gestori vedono solo la lista
    // dei colleghi (read-only) — utile per coordinarsi. Su Solo/Studio l'apertura
    // della pagina mostra l'upsell "Passa a Team" come prima.
    ...(canSeeMembers ? [{
      name: 'Membri',
      href: '/settings/members',
      icon: Users,
    }] : []),
    ...(isOwner ? [{
      name: 'Abbonamento',
      href: '/settings/billing',
      icon: Crown,
    }, {
      name: 'Store librerie',
      href: '/store',
      icon: StoreIcon,
    }] : []),
    // Admin DryConfig: solo staff DryConfig (riga in public.app_admins).
    // É una voce extra-organizzazione: prescinde dal currentRole dell’org corrente.
    ...(isAppAdmin ? [{
      name: 'Admin DryConfig',
      href: '/admin',
      icon: Wand2,
    }] : []),
  ];

  const renderItem = (item: NavItem) => (
    <SidebarMenuItem key={item.name}>
      <SidebarMenuButton asChild isActive={location.pathname === item.href} size="sm">
        <Link
          to={item.href}
          className="flex items-center gap-2"
          data-tour={`nav-${item.href.replace(/^\//, '').replace(/\//g, '-')}`}
          data-tour-id={`nav-${item.href.replace(/^\//, '').replace(/\//g, '-')}`}
        >
          <item.icon className="h-4 w-4" />
          <span className="text-sm flex-1">{item.name}</span>
          {item.badge !== undefined && (
            <span
              className="ml-auto h-4 min-w-[1rem] px-1 rounded-full bg-primary/15 text-primary text-[10px] font-semibold tabular-nums inline-flex items-center justify-center"
              title={item.badgeTitle}
            >
              {item.badge > 99 ? '99+' : item.badge}
            </span>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );

  return (
    <Sidebar>
      <SidebarHeader className="p-4 flex items-center justify-center">
        <Logo size={28} />
      </SidebarHeader>
      <SidebarContent>
        {/* Cluster LAVORO */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
            Lavoro
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {navWork.map(renderItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Cluster CONFIGURATORE */}
        <SidebarGroup>
          <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
            Configuratore
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* "Configura" parent con Parete e Controsoffitto */}
              <SidebarMenuItem>
                <SidebarMenuButton
                  size="sm"
                  isActive={configureActive}
                  className="cursor-default"
                  onClick={(e) => e.preventDefault()}
                  data-tour="nav-configure"
                  data-tour-id="nav-configure"
                >
                  <Wrench className="h-4 w-4" />
                  <span className="text-sm flex-1">Configura</span>
                </SidebarMenuButton>
                <SidebarMenuSub>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={location.pathname === '/configurator'}>
                      <Link to="/configurator" className="flex items-center gap-2">
                        <PanelLeft className="h-3.5 w-3.5" />
                        <span>Parete</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                  <SidebarMenuSubItem>
                    <SidebarMenuSubButton asChild isActive={location.pathname === '/ceilings'}>
                      <Link to="/ceilings" className="flex items-center gap-2">
                        <PanelTop className="h-3.5 w-3.5" />
                        <span>Controsoffitto</span>
                      </Link>
                    </SidebarMenuSubButton>
                  </SidebarMenuSubItem>
                </SidebarMenuSub>
              </SidebarMenuItem>
              {navCatalog.map(renderItem)}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Cluster AMMINISTRAZIONE (solo se ci sono voci visibili per il ruolo) */}
        {navAdmin.length > 0 && (
          <SidebarGroup>
            <SidebarGroupLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70">
              Amministrazione
            </SidebarGroupLabel>
            <SidebarGroupContent>
              <SidebarMenu>
                {navAdmin.map(renderItem)}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
      </SidebarContent>
    </Sidebar>
  );
};

export default AppSidebar;
